<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { computed, onMounted, ref } from 'vue';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { toast } from '@/ui-plus/sonner';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';

type PendingResponse = ResType<
    typeof dashboardApiClient.website.partner_support.store_pending[':websiteId']['$get']
>["data"];
type DeliveryteamPendingResponse = ResType<
    typeof dashboardApiClient.website.partner_support.deliveryteam_pending[':websiteId']['$get']
>["data"];

const pending = ref<PendingResponse | null>(null);
const deliveryteamPending = ref<DeliveryteamPendingResponse | null>(null);

const totalsByCurrency = computed(() => {
    const totals = new Map<string, number>();
    for (const row of [...(pending.value ?? []), ...(deliveryteamPending.value ?? [])]) {
        totals.set(row.currency, (totals.get(row.currency) ?? 0) + Number(row.amount));
    }
    return [...totals.entries()].map(([currency, amount]) => ({ currency, amount }));
});

onMounted(async () => {
    const [storeRes, deliveryteamRes] = await Promise.all([
        (await dashboardApiClient.website.partner_support.store_pending[':websiteId'].$get({
            param: { websiteId: currentWebsiteId.value || '' }
        })).json(),
        (await dashboardApiClient.website.partner_support.deliveryteam_pending[':websiteId'].$get({
            param: { websiteId: currentWebsiteId.value || '' }
        })).json(),
    ]);
    if (storeRes.error) {
        console.error(storeRes.error);
        toast.error('Failed to load pending stores');
    } else {
        pending.value = storeRes.data.toSorted((a, b) => Number(b.amount) - Number(a.amount));
    }
    if (deliveryteamRes.error) {
        console.error(deliveryteamRes.error);
        toast.error('Failed to load pending delivery teams');
    } else {
        deliveryteamPending.value = deliveryteamRes.data.toSorted((a, b) => Number(b.amount) - Number(a.amount));
    }
});
</script>

<template>
    <div class="mt-8"
         v-if="pending">
        <div v-if="totalsByCurrency.length > 0"
             class="pt-2">
            <h3 class="font-medium mt-2 mb-2">Total</h3>
            <div class="space-y-3">
                <div v-for="total in totalsByCurrency"
                     :key="total.currency"
                     class="flex items-center justify-between p-3 border rounded-lg">
                    <p class="font-medium">{{ total.currency.toUpperCase() }}</p>
                    <div class="text-right text-lg font-bold">
                        {{ formatCurrency(total.amount, total.currency) }}
                    </div>
                </div>
            </div>
        </div>

        <h2 class="text-lg font-bold mt-8">Stores with pending fees</h2>
        <p class="text-sm text-muted-foreground">
            These stores have unpaid fees that have not been paid yet.
        </p>
        <div v-if="pending.length === 0"
             class="text-center py-8 text-muted-foreground">
            All stores have paid their fees
        </div>
        <div v-else
             class="space-y-3">
            <div v-for="store in pending"
                 :key="store.storeId || ''"
                 class="flex items-center justify-between p-3 border rounded-lg">
                <div>
                    <p class="font-medium">
                        {{ store.storeName }}
                    </p>
                </div>
                <div class="text-right text-lg font-bold">
                    {{ formatCurrency(store.amount, store.currency) }}
                </div>
            </div>
        </div>

        <h2 class="text-lg font-bold mt-8">Delivery teams with pending fees</h2>
        <p class="text-sm text-muted-foreground">
            These delivery teams have unpaid fees that have not been paid yet.
        </p>
        <div v-if="!deliveryteamPending || deliveryteamPending.length === 0"
             class="text-center py-8 text-muted-foreground">
            All delivery teams have paid their fees
        </div>
        <div v-else
             class="space-y-3">
            <div v-for="deliveryteam in deliveryteamPending"
                 :key="deliveryteam.deliveryteamId || ''"
                 class="flex items-center justify-between p-3 border rounded-lg">
                <div>
                    <p class="font-medium">
                        {{ deliveryteam.deliveryteamName }}
                    </p>
                </div>
                <div class="text-right text-lg font-bold">
                    {{ formatCurrency(deliveryteam.amount, deliveryteam.currency) }}
                </div>
            </div>
        </div>
    </div>
    <div v-else>
        Loading...
    </div>
</template>